import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { encodeOpenMetadataFqnSafe } from '@/shared/lib/format';
import type { JsonPatchOperation, TagLabel, TagLabelType, TagSource, TagState } from '@/shared/types';

export const updateColumnTags = async (
  tableFqn: string,
  columnName: string,
  tagFQNs: string[],
  source: TagSource
): Promise<void> => {
  const encodedFqn = encodeOpenMetadataFqnSafe(tableFqn);

  const tableResponse = await openMetadataApiClient.get(`/tables/name/${encodedFqn}`, {
    params: {
      fields: 'columns,tags',
      include: 'all',
    },
  });

  const columns = tableResponse.data.columns || [];
  const columnIndex = columns.findIndex((col: { name: string }) => col.name === columnName);

  if (columnIndex === -1) {
    throw new Error(`Column ${columnName} not found in table ${tableFqn}`);
  }

  const currentColumn = columns[columnIndex];
  const currentTags = currentColumn.tags || [];

  const glossaryTags = currentTags.filter((tag: TagLabel) => tag.source === 'Glossary');
  const classificationTags = currentTags.filter((tag: TagLabel) => tag.source === 'Classification');

  const newSourceTags = tagFQNs.map((tagFQN): TagLabel => {
    const name = tagFQN.split('.').pop() || tagFQN;
    return {
      tagFQN,
      source,
      name,
      labelType: 'Manual' as TagLabelType,
      state: 'Confirmed' as TagState,
    };
  });

  const newTags: TagLabel[] =
    source === 'Glossary' ? [...newSourceTags, ...classificationTags] : [...glossaryTags, ...newSourceTags];

  const patchOperations: JsonPatchOperation[] = [
    {
      op: 'replace',
      path: `/columns/${columnIndex}/tags`,
      value: newTags,
    },
  ];

  await openMetadataApiClient.patch(`/tables/name/${encodedFqn}`, patchOperations, {
    headers: {
      'Content-Type': 'application/json-patch+json',
    },
  });
};
